/**
 * Sesión VAD: envía frames PCM al worker y recibe segmentos / eventos de voz.
 */

/**
 * @returns {string}
 */
function resolveVadWorkerUrl() {
  try {
    let path = '/voice/sileroVad.worker.js'
    try {
      const base =
        (typeof process !== 'undefined' &&
          process.env &&
          process.env.PUBLIC_URL) ||
        ''
      const prefix = String(base || '').replace(/\/+$/, '')
      path = `${prefix}/voice/sileroVad.worker.js`.replace(/\/{2,}/g, '/')
      if (!path.startsWith('/')) path = `/${path}`
    } catch (e) {
      path = '/voice/sileroVad.worker.js'
    }
    return new URL(path, window.location.href).href
  } catch (e) {
    return './voice/sileroVad.worker.js'
  }
}

export class VadSession {
  /**
   * @param {{
   *   onSegment?: (pcm: Float32Array, meta: { sampleRate: number, reason: string }) => void,
   *   onSpeechStart?: () => void,
   *   onSpeechEnd?: () => void,
   *   onReady?: (engine: string) => void,
   *   onError?: (err: Error) => void,
   * }} [opts]
   */
  constructor(opts = {}) {
    this.onSegment = opts.onSegment || null
    this.onSpeechStart = opts.onSpeechStart || null
    this.onSpeechEnd = opts.onSpeechEnd || null
    this.onReady = opts.onReady || null
    this.onError = opts.onError || null
    /** @type {Worker|null} */
    this._worker = null
    this._ready = false
    this._engine = 'energy'
  }

  /**
   * @returns {Promise<string>} engine
   */
  async init() {
    if (this._worker && this._ready) return this._engine
    if (typeof Worker === 'undefined') {
      throw new Error('Web Worker no disponible')
    }
    await this.destroy()
    const worker = new Worker(resolveVadWorkerUrl())
    this._worker = worker

    const ready = new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error('Timeout init VAD'))
      }, 15000)
      worker.onmessage = (ev) => {
        const msg = ev && ev.data
        if (!msg) return
        if (msg.type === 'ready') {
          clearTimeout(timer)
          this._ready = true
          this._engine = msg.engine || 'energy'
          if (this.onReady) this.onReady(this._engine)
          resolve(this._engine)
          return
        }
        if (msg.type === 'speech_start' && this.onSpeechStart) {
          this.onSpeechStart()
        }
        if (msg.type === 'speech_end' && this.onSpeechEnd) {
          this.onSpeechEnd()
        }
        if (msg.type === 'segment' && msg.pcm && this.onSegment) {
          const pcm = new Float32Array(msg.pcm)
          this.onSegment(pcm, {
            sampleRate: msg.sampleRate || 16000,
            reason: msg.reason || 'silence',
          })
        }
        if (msg.type === 'error' && this.onError) {
          this.onError(new Error(msg.message || 'VAD error'))
        }
      }
      worker.onerror = (err) => {
        clearTimeout(timer)
        reject(err || new Error('VAD worker error'))
      }
    })

    worker.postMessage({ type: 'init' })
    return ready
  }

  /**
   * @param {Float32Array} pcm
   */
  pushFrame(pcm) {
    if (!this._worker || !pcm || !pcm.length) return
    try {
      const copy = pcm.slice(0)
      this._worker.postMessage(
        { type: 'frame', pcm: copy.buffer },
        [copy.buffer]
      )
    } catch (e) {
      try {
        this._worker.postMessage({ type: 'frame', pcm: pcm.buffer })
      } catch (e2) {
        /* ignore */
      }
    }
  }

  flush() {
    try {
      if (this._worker) this._worker.postMessage({ type: 'flush' })
    } catch (e) {
      /* ignore */
    }
  }

  reset() {
    try {
      if (this._worker) this._worker.postMessage({ type: 'reset' })
    } catch (e) {
      /* ignore */
    }
  }

  async destroy() {
    this._ready = false
    if (!this._worker) return
    try {
      this._worker.terminate()
    } catch (e) {
      /* ignore */
    }
    this._worker = null
  }
}

/**
 * Lógica de segmentación pura (tests / sin worker).
 * @param {number[]} probs secuencia de probs por frame
 * @param {{ silenceFrames?: number, maxSpeechFrames?: number }} [opts]
 * @returns {{ events: string[], cutAt: number[] }}
 */
export function simulateVadSegmentation(probs, opts = {}) {
  const silenceFrames = opts.silenceFrames != null ? opts.silenceFrames : 4
  const maxSpeechFrames = opts.maxSpeechFrames != null ? opts.maxSpeechFrames : 30
  let speaking = false
  let silenceRun = 0
  let speechFrames = 0
  const events = []
  const cutAt = []
  for (let i = 0; i < probs.length; i++) {
    const isSpeech = probs[i] >= 0.45
    if (isSpeech) {
      if (!speaking) {
        speaking = true
        events.push('speech_start')
      }
      speechFrames += 1
      silenceRun = 0
      if (speechFrames >= maxSpeechFrames) {
        events.push('segment:max_window')
        cutAt.push(i)
        speechFrames = Math.ceil(5) // overlap ~500ms / 100ms
      }
    } else if (speaking) {
      silenceRun += 1
      if (silenceRun >= silenceFrames) {
        speaking = false
        events.push('speech_end')
        events.push('segment:silence')
        cutAt.push(i)
        speechFrames = 0
        silenceRun = 0
      }
    }
  }
  return { events, cutAt }
}

export default VadSession
